"use client";

import { useCallback, useMemo, useState, useTransition } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/format";
import type { FlexibleTestimonial, Pagination } from "@/lib/dto";
import ReviewCardList from "@/components/card/ReviewCardList";
import { testimonialCountry, testimonialRating } from "@/lib/testimonial";

type PackageOption = { slug: string; title: string };

type Props = {
  initialItems: FlexibleTestimonial[];
  initialPagination: Pagination;
  packages: PackageOption[];
};

export function TestimonialListClient({
  initialItems,
  initialPagination,
  packages,
}: Props) {
  const [items, setItems] = useState<FlexibleTestimonial[]>(initialItems);
  const [pagination, setPagination] = useState<Pagination>(initialPagination);
  const [activePackage, setActivePackage] = useState("");
  const [ratings, setRatings] = useState<Set<number>>(new Set());
  const [activeCountry, setActiveCountry] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  const fetchPage = useCallback(
    (page: number, pkg: string, append = false) => {
      startTransition(async () => {
        const p = new URLSearchParams({ page: String(page), limit: "8" });
        if (pkg) p.set("package", pkg);
        const res = await fetch(`/api/testimonials?${p}`);
        const data = await res.json();
        setItems((prev) => (append ? [...prev, ...data.items] : data.items));
        setPagination(data.pagination);
      });
    },
    [],
  );

  function handlePackage(slug: string) {
    setActivePackage(slug);
    fetchPage(1, slug);
  }

  function loadMore() {
    fetchPage(pagination.page + 1, activePackage, true);
  }

  function toggleRating(stars: number) {
    setRatings((prev) => {
      const next = new Set(prev);
      if (next.has(stars)) next.delete(stars);
      else next.add(stars);
      return next;
    });
  }

  // Rating/country counts + list filtering apply within the currently loaded
  // page(s) — the API has no rating/country query param, only `package`.
  const countryCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const t of items) {
      const c = testimonialCountry(t);
      if (c) map.set(c, (map.get(c) ?? 0) + 1);
    }
    return [...map.entries()].sort((a, b) => b[1] - a[1]);
  }, [items]);

  const ratingCounts = useMemo(() => {
    const map = new Map<number, number>();
    for (const t of items) {
      const r = Math.round(testimonialRating(t) ?? 5);
      map.set(r, (map.get(r) ?? 0) + 1);
    }
    return map;
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((t) => {
      if (ratings.size && !ratings.has(Math.round(testimonialRating(t) ?? 5)))
        return false;
      if (activeCountry && testimonialCountry(t) !== activeCountry)
        return false;
      return true;
    });
  }, [items, ratings, activeCountry]);

  return (
    <div className="grid grid-cols-12 gap-6 md:gap-8">
      <aside className="col-span-12 lg:col-span-4">
        <div className="rounded-md bg-white p-6 shadow-base">
          <h3 className="mb-6 text-base font-bold text-muted">Filter by:</h3>

          {packages.length ? (
            <div className="box">
              <label
                htmlFor="package-filter"
                className="mb-2 block font-secondary text-base font-bold text-body/90"
              >
                By Package
              </label>
              <div className="relative rounded-md border border-border bg-white">
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-primary">
                  <ChevronDown size={16} aria-hidden />
                </span>
                <select
                  id="package-filter"
                  value={activePackage}
                  onChange={(e) => handlePackage(e.target.value)}
                  className="w-full appearance-none rounded border-none bg-transparent py-2 pl-[18px] pr-[52px] font-secondary text-sm font-bold text-primary focus:outline-none focus:ring-0"
                >
                  <option value="">Select a Package</option>
                  {packages.map((p) => (
                    <option key={p.slug} value={p.slug}>
                      {p.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : null}

          <div className="box mt-6">
            <h3 className="module-title mb-3">By Ratings</h3>
            <ul className="space-y-1">
              {[5, 4, 3, 2, 1].map((stars) => (
                <li key={stars}>
                  <label className="form-check flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={ratings.has(stars)}
                      onChange={() => toggleRating(stars)}
                    />
                    <span className="form-check-label flex items-center gap-1.5 text-sm">
                      <i className={`ratings__${stars}`} aria-hidden />
                      <span className="text-muted-soft">
                        ({ratingCounts.get(stars) ?? 0})
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </div>

          {countryCounts.length ? (
            <div className="box mt-6">
              <h3 className="module-title mb-3">By Country</h3>
              <ul className="space-y-1">
                {countryCounts.map(([country, count]) => (
                  <li key={country}>
                    <label className="form-check flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={activeCountry === country}
                        onChange={() =>
                          setActiveCountry((prev) =>
                            prev === country ? null : country,
                          )
                        }
                      />
                      <span className="form-check-label text-md font-medium text-headings/90">
                        {country}{" "}
                        <span className="text-muted-soft">({count})</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </aside>

      <div className="col-span-12 lg:col-span-8">
        <div className="mb-6 rounded-md border border-line bg-light px-6 py-3 text-sm font-medium text-body">
          Showing {filtered.length} reviews
        </div>

        {filtered.length ? (
          <ul className={cn("grid gap-y-6", isPending && "opacity-50")}>
            {filtered.map((t) => (
              <li key={t.id}>
                <ReviewCardList renderData={t} />
              </li>
            ))}
          </ul>
        ) : (
          <div className="rounded-md border border-dashed border-line py-16 text-center text-sm text-muted">
            No reviews match your filters.
          </div>
        )}

        {pagination.has_next ? (
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={loadMore}
              disabled={isPending}
              className="btn btn-primary inline-flex items-center gap-x-2 rounded-md text-sm font-extrabold hover:bg-primary-100 disabled:opacity-60"
            >
              {isPending ? (
                <Loader2 size={15} className="animate-spin" aria-hidden />
              ) : null}
              Load More
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
